import {
  createGridGeometry,
  scaleTileExtent,
} from './projection';

const SQRT3_OVER_2 = Math.sqrt(3) / 2;
const TRIANGLE_EDGE_FACINGS = [0, 120, 240] as const;
export const TRIANGLE_PROJECTION_SCALE = 1.35;

export const TRIANGLE_PROJECTION = {
  scale: TRIANGLE_PROJECTION_SCALE,
  stepXFactor: 0.5,
  stepYFactor: SQRT3_OVER_2,
  renderWidthFactor: 1,
  renderHeightFactor: SQRT3_OVER_2,
} as const;

function pointsUp(q: number, r: number): boolean {
  return ((q + r) & 1) === 0;
}

export const triangleGrid = createGridGeometry({
  projection: TRIANGLE_PROJECTION,
  directions: 4,
  facingAngles: [0, 90, 180, 270],
  rotationStep: 90,
  movementAngleTolerance: 45,
  keyDirections: {
    ArrowUp: { dx: 0, dy: -1, facing: 0 },
    ArrowDown: { dx: 0, dy: 1, facing: 180 },
    ArrowLeft: { dx: -1, dy: 0, facing: 270 },
    ArrowRight: { dx: 1, dy: 0, facing: 90 },
  },
  resolveTurnFacingForKey(key: string, x: number, y: number) {
    const up = pointsUp(x, y);
    if (key === 'q') {
      // Left edge midpoint from centroid depends on triangle orientation.
      return up ? 240 : 300;
    }
    if (key === 'e') {
      // Right edge midpoint from centroid depends on triangle orientation.
      return up ? 120 : 60;
    }
    return null;
  },
  neighbors(q: number, r: number) {
    return [
      { x: q, y: r - 1 },
      { x: q + 1, y: r },
      { x: q, y: r + 1 },
      { x: q - 1, y: r },
    ];
  },
  distance(x1: number, y1: number, x2: number, y2: number) {
    return Math.max(Math.abs(x1 - x2), Math.abs(y1 - y2));
  },
  isAdjacent(x1: number, y1: number, x2: number, y2: number) {
    const dx = Math.abs(x1 - x2);
    const dy = Math.abs(y1 - y2);
    return dx + dy === 1;
  },
  drawTilePath(ctx: CanvasRenderingContext2D, cx: number, cy: number, size: number, q?: number, r?: number) {
    const scaled = scaleTileExtent(size, TRIANGLE_PROJECTION);
    const side = scaled * 2;
    const half = side / 2;
    const halfHeight = side * SQRT3_OVER_2 * 0.5;
    const up = pointsUp(q ?? 0, r ?? 0);

    ctx.beginPath();
    if (up) {
      ctx.moveTo(cx, cy - halfHeight);
      ctx.lineTo(cx + half, cy + halfHeight);
      ctx.lineTo(cx - half, cy + halfHeight);
    } else {
      ctx.moveTo(cx - half, cy - halfHeight);
      ctx.lineTo(cx + half, cy - halfHeight);
      ctx.lineTo(cx, cy + halfHeight);
    }
    ctx.closePath();
  },
  tileCentroid(size: number, q?: number, r?: number) {
    const up = pointsUp(q ?? 0, r ?? 0);
    const centerY = size / 2;
    // drawTilePath uses half-size triangles with the triangle projection scale.
    // This reproduces the same half-height used there so centroid aligns exactly.
    const halfHeight = scaleTileExtent(size * 0.5, TRIANGLE_PROJECTION) * SQRT3_OVER_2;
    const centroidOffsetY = halfHeight / 3;
    return {
      x: size / 2,
      y: up ? centerY + centroidOffsetY : centerY - centroidOffsetY,
    };
  },
});
